import { formatCentsAsUsd, formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { formatHoursDurationCompact } from "@src/appV2/redesign/utils/formatHoursDurationCompact";

/**
 * Formats a duration in hours to a human-readable string.
 * Handles edge cases like rounding to 60 minutes.
 *
 * @param hours - Duration in hours as a decimal (e.g., 8.5 for 8h 30m)
 * @returns Formatted string like "12h 47m" or "8h"
 *
 * @example
 * formatDurationFromHours(12.78) // "12h 47m"
 * formatDurationFromHours(8.0)   // "8h"
 * formatDurationFromHours(0.9999) // "1h" (not "0h 60m")
 */
export function formatDurationFromHours(hours: number): string {
  return formatHoursDurationCompact(hours, { omitZeroMinutes: true });
}

/**
 * @deprecated Use formatDurationFromHours instead for explicit unit naming.
 */
export const formatDuration = formatDurationFromHours;

/**
 * Formats a currency amount in dollars to USD string using Intl.NumberFormat.
 * Uses the canonical money formatting utility for consistency.
 *
 * @param dollars - Amount in dollars (e.g., 482.82)
 * @returns Formatted string like "$482.82"
 */
export function formatUsdFromDollars(dollars: number): string {
  return formatDollarsAsUsd(dollars);
}

/**
 * Formats a currency amount in cents to USD string.
 * Uses the canonical formatCentsAsUsd utility which handles
 * the cents-to-dollars conversion internally.
 * Preferred for calculations to avoid float math issues.
 *
 * @param cents - Amount in cents (e.g., 48282 for $482.82)
 * @returns Formatted string like "$482.82"
 */
export function formatUsdFromCents(cents: number): string {
  return formatCentsAsUsd(cents);
}

/**
 * @deprecated Use formatUsdFromDollars or formatUsdFromCents instead for explicit unit naming.
 */
export const formatCurrency = formatUsdFromDollars;
